import React, { useCallback } from 'react';
import { Mic, Square, Loader2, MicOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import useVoiceRecorder from '../hooks/useVoiceRecorder';

/**
 * Push-to-talk mic button.
 *
 * Tap to start recording → tap again to stop → calls onUploadStart, then
 * either onTranscript({ transcript, prompts_used }) or onError(code).
 *
 * Props:
 *   onTranscript(payload)  — required: called with successful STT response
 *   onError(code, msg)     — optional: surfaced via toast/inline
 *   uploader               — required: async (blob) => fetch response (lets parent inject axios/auth)
 *   disabled               — disable the button
 */
const VoiceRecorderButton = ({ onTranscript, onError, uploader, disabled }) => {
  const { t } = useTranslation();
  const { state, error, start, stop, cancel, isSupported, elapsedSeconds } = useVoiceRecorder();

  const handleClick = useCallback(async () => {
    if (state === 'recording') {
      const blob = await stop();
      if (!blob || blob.size < 200) {
        cancel();
        onError && onError('no_speech', t('voice.errorNoSpeech'));
        return;
      }
      try {
        const result = await uploader(blob);
        if (result && result.success && result.transcript) {
          onTranscript({ transcript: result.transcript, prompts_used: result.prompts_used });
        } else if (result && result.message === 'no_speech') {
          onError && onError('no_speech', t('voice.errorNoSpeech'));
        } else {
          onError && onError('upload_error', t('voice.errorUpload'));
        }
      } catch (err) {
        const status = err && err.response && err.response.status;
        if (status === 429) {
          // Backend returns { detail: { error: 'voice_limit' | 'daily_limit', ... } }
          const detailError =
            err && err.response && err.response.data && err.response.data.detail
              ? err.response.data.detail.error
              : null;
          if (detailError === 'voice_limit') {
            onError && onError('voice_limit', t('voice.errorVoiceLimit'));
          } else {
            onError && onError('daily_limit', t('voice.errorDailyLimit'));
          }
        } else {
          onError && onError('upload_error', t('voice.errorUpload'));
        }
      }
      return;
    }
    const ok = await start();
    if (!ok && error === 'permission') {
      onError && onError('permission', t('voice.errorPermission'));
    } else if (!ok && error === 'unsupported') {
      onError && onError('unsupported', t('voice.errorUnsupported'));
    }
  }, [state, stop, cancel, start, uploader, onTranscript, onError, error, t]);

  if (!isSupported) {
    return (
      <Button
        variant="ghost"
        className="hussar-btn-icon"
        disabled
        title={t('voice.errorUnsupported')}
        data-testid="voice-record-btn-unsupported"
      >
        <MicOff className="w-5 h-5" />
        <span className="hussar-btn-label">{t('voice.idle')}</span>
      </Button>
    );
  }

  const isRecording = state === 'recording';
  const isProcessing = state === 'processing' || state === 'requesting';
  const isBusy = disabled || isProcessing;

  let icon;
  let label;
  let className = 'hussar-btn-icon';
  if (isRecording) {
    icon = <Square className="w-5 h-5" />;
    label = `${t('voice.recording')} ${formatElapsed(elapsedSeconds)}`;
    className = 'hussar-btn-icon hussar-voice-recording';
  } else if (isProcessing) {
    icon = <Loader2 className="w-5 h-5 animate-spin" />;
    label = t('voice.processing');
  } else {
    icon = <Mic className="w-5 h-5" />;
    label = t('voice.idle');
  }

  return (
    <Button
      variant="ghost"
      className={className}
      onClick={handleClick}
      disabled={isBusy}
      data-testid={isRecording ? 'voice-record-btn-stop' : 'voice-record-btn-start'}
      title={isRecording ? t('voice.stop') : t('voice.startHint')}
    >
      {icon}
      <span className="hussar-btn-label">{label}</span>
    </Button>
  );
};

const formatElapsed = (sec) => {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

export default VoiceRecorderButton;
